import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { BayarBadge } from "../components/BayarDialogs";
import { api, errMsg } from "../lib/api";
import { CloverLogo } from "../components/CloverLogo";
import { fmtDate, fmtDateTime, num, rp, waPhone } from "../lib/format";

const Line = ({ label, value, tone = "text-gray-900", strong, testid }) => (
  <div className={`flex items-baseline justify-between gap-4 py-2.5 ${strong ? "border-t-2 border-gray-900 pt-4" : "border-b border-dashed border-gray-200"}`}>
    <span className={`text-sm ${strong ? "font-bold text-gray-900" : "text-gray-600"}`}>{label}</span>
    <span data-testid={testid} className={`font-mono tabular-nums ${strong ? "text-2xl font-bold" : "text-base font-semibold"} ${tone}`}>{value}</span>
  </div>
);

export default function SlipPage() {
  const { period, anakId } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/payroll/${period}/slip/${anakId}`).then((r) => setS(r.data)).catch((e) => { setErr(errMsg(e)); toast.error(errMsg(e)); });
  }, [period, anakId]);

  if (err) return <p data-testid="slip-error" className="text-sm text-red-600">{err}</p>;
  if (!s) return <p className="text-sm text-gray-500">Memuat…</p>;
  const r = s.row;
  const paid = r.bayar?.status === "sudah";
  const waText = [
    "*Slip Gaji — CLOVER MANAGEMENT*",
    `Nama: ${r.anak_nama}`,
    `Periode: ${s.label}`,
    `Voucher: ${num(r.voucher)} × ${rp(s.tarif)}`,
    `Gaji Kotor: ${rp(r.gaji_kotor)}`,
    `Potongan Kasbon: ${rp(r.potongan)}`,
    `*Gaji Bersih: ${rp(r.gaji_bersih)}*`,
    `Sisa Kasbon: ${rp(r.sisa_kasbon)}`,
    paid ? `Status: Sudah dibayar (${fmtDate(r.bayar.tanggal_bayar)}, ${r.bayar.cara_label})` : "Status: Belum dibayar",
  ].join("\n");
  const waUrl = `https://wa.me/${waPhone(s.no_hp)}?text=${encodeURIComponent(waText)}`;

  return (
    <div className="max-w-xl">
      <div className="no-print mb-5 flex flex-wrap gap-2">
        <Button data-testid="slip-back-btn" variant="ghost" className="h-10" onClick={() => navigate(-1)}><ArrowLeft className="mr-1.5 h-4 w-4" />Kembali</Button>
        <div className="flex-1" />
        <Button data-testid="slip-print-btn" variant="outline" className="h-10 rounded-full" onClick={() => window.print()}><Printer className="mr-1.5 h-4 w-4" />Cetak</Button>
        <Button asChild data-testid="slip-wa-btn" className="h-10 rounded-full bg-[#1FAF5A] hover:bg-[#178a47]">
          <a href={waUrl} target="_blank" rel="noopener noreferrer"><MessageCircle className="mr-1.5 h-4 w-4" />Bagikan WhatsApp</a>
        </Button>
      </div>

      <article data-testid="slip-card" className="print-plain fade-up rounded-2xl border border-gray-200 bg-white p-4 sm:p-8">
        <header className="mb-6 flex items-start justify-between gap-4 border-b border-gray-200 pb-5">
          <div className="flex items-center gap-3">
            <CloverLogo className="h-10 w-12 shrink-0" testid="slip-logo" />
            <div>
              <p className="font-heading text-[11px] font-semibold uppercase tracking-[0.2em] text-primary">CLOVER MANAGEMENT</p>
              <h1 className="mt-0.5 font-heading text-2xl font-semibold text-gray-900">Slip Gaji</h1>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 text-right">
            <BayarBadge bayar={r.bayar} testid="slip-status" />
            {paid && <p data-testid="slip-bayar-info" className="text-xs text-gray-600">{fmtDate(r.bayar.tanggal_bayar)} · {r.bayar.cara_label}</p>}
          </div>
        </header>
        <dl className="mb-4 grid grid-cols-2 gap-4 text-sm">
          <div><dt className="text-[11px] uppercase tracking-wider text-gray-400">Nama</dt><dd data-testid="slip-nama" className="font-semibold text-gray-900">{r.anak_nama}</dd></div>
          <div><dt className="text-[11px] uppercase tracking-wider text-gray-400">Periode</dt><dd data-testid="slip-periode" className="font-semibold text-gray-900">{s.label}</dd></div>
          <div><dt className="text-[11px] uppercase tracking-wider text-gray-400">Perusahaan</dt><dd className="text-gray-900">{r.perusahaan_nama || "-"}</dd></div>
          <div><dt className="text-[11px] uppercase tracking-wider text-gray-400">Tarif voucher</dt><dd className="font-mono text-gray-900">{rp(s.tarif)}</dd></div>

        </dl>
        <Line label="Jumlah voucher" value={num(r.voucher)} testid="slip-voucher" />
        <Line label="Gaji kotor" value={rp(r.gaji_kotor)} testid="slip-gaji-kotor" />
        <Line label="Kasbon periode lalu" value={rp(r.kasbon_sebelumnya)} tone="text-gray-500" />
        <Line label="Kasbon periode ini" value={rp(r.kasbon_periode)} tone="text-gray-500" />
        <Line label="Potongan kasbon" value={`−${rp(r.potongan)}`} tone="text-red-600" testid="slip-potongan" />
        <Line label="Gaji bersih" value={rp(r.gaji_bersih)} tone="text-primary" strong testid="slip-gaji-bersih" />
        <div className="mt-4 flex items-center justify-between rounded-lg bg-amber-50 px-4 py-3">
          <span className="text-sm font-medium text-amber-800">Sisa kasbon (dibawa ke periode berikutnya)</span>
          <span data-testid="slip-sisa-kasbon" className="font-mono font-semibold text-amber-800">{rp(r.sisa_kasbon)}</span>
        </div>
        {paid && r.bayar.dicatat_at && <p className="mt-6 text-xs text-gray-400">Dicatat dibayar {fmtDateTime(r.bayar.dicatat_at)}</p>}
        {paid && !r.bayar.dicatat_at && s.paid_at && <p className="mt-6 text-xs text-gray-400">Dibayar {fmtDateTime(s.paid_at)}</p>}
      </article>
    </div>
  );
}
